import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, articles } from '@/mocks/data';

const colorMap: Record<string, { bg: string; text: string; activeBg: string }> = {
  accent: { bg: 'bg-accent-100', text: 'text-accent-700', activeBg: 'bg-accent-500' },
  primary: { bg: 'bg-primary-100', text: 'text-primary-700', activeBg: 'bg-primary-500' },
  secondary: { bg: 'bg-secondary-100', text: 'text-secondary-700', activeBg: 'bg-secondary-500' },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'hero-guide';

  const currentCategory = categories.find((c) => c.slug === currentSlug);
  const filteredArticles = articles.filter((a) => a.category === currentSlug);
  const catColors = currentCategory ? colorMap[currentCategory.color] || colorMap.primary : colorMap.primary;

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://lolguide.readdy.ai/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: currentCategory?.name || '攻略分类',
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <Navbar />
      <main className="pt-16">
        <section className="bg-background-100 py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory?.name || '攻略分类' },
              ]}
            />

            <div className="text-center mb-10">
              <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-3">
                {currentCategory?.name || '攻略分类'}
              </h1>
              <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                {currentCategory?.description || '浏览所有撸啊撸攻略文章'}
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-2 mb-12">
              {categories.map((cat) => {
                const cColors = colorMap[cat.color] || colorMap.primary;
                const isActive = cat.slug === currentSlug;
                return (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap no-underline cursor-pointer ${
                      isActive
                        ? `${cColors.activeBg} text-background-50`
                        : `${cColors.bg} ${cColors.text} hover:opacity-80`
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5`}></i>
                    {cat.name}
                  </Link>
                );
              })}
            </div>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 no-underline cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        title={article.title}
                      />
                      {article.hot && (
                        <div className="absolute top-3 left-3">
                          <span className="px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium">
                            热门
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime} 分钟
                        </span>
                        <span>{article.publishDate}</span>
                      </div>
                      <h2 className="font-heading text-base text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-4">
                        {article.tags.slice(0, 3).map((tag: string) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-200">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类下暂无文章，请查看其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}